// Copyright (c) 2020-2026 Life and Soft

import React from "react";

const Dashboard = React.lazy(() => import("./views/Dashboard/Dashboard"));
const Application = React.lazy(
  () => import("./views/Administration/Application/Application"),
);
const Help = React.lazy(() => import("./views/Help/Help"));
const Versions = React.lazy(() => import("./views/Versions/Versions"));
const Profile = React.lazy(() => import("./views/Profile/Profile"));
const AddUser = React.lazy(
  () => import("./views/Administration/Application/UserManagement/AddUser"),
);
const AnalysisAdmin = React.lazy(
  () => import("./views/Administration/Analysis/Analysis"),
);
const AddEditWorkflow = React.lazy(
  () => import("./views/Administration/Analysis/AddEditWorkflow"),
);
const Logs = React.lazy(() => import("./views/Administration/Logs/Logs"));
const Reporting = React.lazy(
  () => import("./views/Administration/Reporting/Reporting"),
);
const Project = React.lazy(
  () => import("./views/Administration/Projects/Projects"),
);
const AddProject = React.lazy(
  () => import("./views/Administration/Projects/AddProject"),
);
const Analysis = React.lazy(() => import("./views/Analysis/Analysis"));
const AnalysisDetails = React.lazy(
  () => import("./views/Analysis/AnalysisDetails/AnalysisDetails"),
);
const NewAnalysis = React.lazy(
  () => import("./views/Analysis/NewAnalysis/NewAnalysis"),
);
const SampleDetails = React.lazy(() => import("./views/Samples/SampleDetails"));
const Runs = React.lazy(() => import("./views/Runs/Runs/Runs"));
const RunDetails = React.lazy(
  () => import("./views/Runs/RunDetails/RunDetails"),
);
const Fastq = React.lazy(() => import("./views/Runs/Fastq/Fastq"));
const Search = React.lazy(() => import("./views/Search/Search"));
const APIDoc = React.lazy(() => import("./views/API/APIDoc"));
const ForgotPassword = React.lazy(
  () => import("./views/Password/ForgotPassword"),
);
const SubPanels = React.lazy(() => import("./views/SubPanels/SubPanels"));
const JBrowse = React.lazy(() => import("./views/JBrowse/JBrowse"));

function AuditLog() {
  return <Logs logType="audit" cardTitle="Logs History" />;
}

function SessionLog() {
  return <Logs logType="session" cardTitle="Connection history" />;
}

// https://github.com/ReactTraining/react-router/tree/master/packages/react-router-config
const routes = [
  { path: "/", exact: true, name: "Home", roles: ["Admin", "Manager", "User"] },
  {
    path: "/dashboard",
    exact: true,
    index: true,
    name: "Dashboard",
    element: Dashboard,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/analysis",
    exact: true,
    name: "Analysis",
    element: Analysis,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/forgotPassword/:id",
    exact: true,
    name: "Forget Password Page",
    element: ForgotPassword,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/analysis/:analysisId",
    exact: true,
    name: "Analysis Details",
    element: AnalysisDetails,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/analysis/:analysisId/samples/:sampleId",
    exact: true,
    name: "Sample Details",
    element: SampleDetails,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/runs",
    exact: true,
    name: "Runs",
    element: Runs,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/projects/:directoryName/runs/:runName",
    exact: true,
    name: "Run Details",
    element: RunDetails,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/projects/:directoryName/runs/:runName/:fastqName",
    exact: true,
    name: "FASTQ Details",
    element: Fastq,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/analysis/new/:directoryName/:runName",
    exact: true,
    name: "New Analysis",
    element: NewAnalysis,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/analysis/new",
    exact: true,
    name: "New Analysis",
    element: NewAnalysis,
    roles: ["Admin", "Manager", "User"],
  },

  {
    path: "/sub-panels",
    exact: true,
    name: "Sub-panels",
    element: SubPanels,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/search",
    exact: true,
    name: "Search",
    element: Search,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/help",
    exact: true,
    name: "Help",
    element: Help,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/versions",
    exact: true,
    name: "Version history",
    element: Versions,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/settings/:id",
    exact: true,
    name: "User settings",
    element: Profile,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/api-doc",
    exact: true,
    name: "API Documentation",
    element: APIDoc,
    roles: ["Admin", "Manager", "User"],
  },
  {
    path: "/jbrowse/:analysisId/sample/:sampleId",
    exact: true,
    name: "JBrowse",
    element: JBrowse,
    roles: ["Admin", "Manager", "User"],
  },
  /** ADMIN PAGES **/
  {
    path: "/administration/application",
    exact: true,
    name: "Application Settings",
    element: Application,
    roles: ["Admin"],
  },
  {
    path: "/administration/application/addUser",
    name: "Add User",
    element: AddUser,
    roles: ["Admin"],
  },
  {
    path: "/administration/analysis",
    exact: true,
    name: "Analysis Settings",
    element: AnalysisAdmin,
    roles: ["Admin"],
  },
  {
    path: "/administration/projects",
    exact: true,
    name: "Projects Settings",
    element: Project,
    roles: ["Admin"],
  },
  {
    path: "/administration/projects/add",
    exact: true,
    name: "Add Project",
    element: AddProject,
    roles: ["Admin"],
  },
  {
    path: "/administration/projects/edit/:projectName",
    exact: true,
    name: "Edit Project",
    element: AddProject,
    roles: ["Admin"],
  },
  {
    path: "/administration/analysis/add",
    exact: true,
    name: "Add Workflow",
    element: AddEditWorkflow,
    roles: ["Admin"],
  },
  {
    path: "/administration/analysis/edit/:workflowName",
    exact: true,
    name: "Edit Workflow",
    element: AddEditWorkflow,
    roles: ["Admin"],
  },
  {
    path: "/administration/reporting",
    exact: true,
    name: "Reporting",
    element: Reporting,
    roles: ["Admin"],
  },
  {
    path: "/administration/logs/audit",
    exact: true,
    name: "Audit Logs",
    element: AuditLog,
    roles: ["Admin"],
  },
  {
    path: "/administration/logs/session",
    exact: true,
    name: "Session Logs",
    element: SessionLog,
    roles: ["Admin"],
  },
];

export default routes;
